import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Clock, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Messaggio inviato con successo! Ti ricontatteremo presto.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Errore nell'invio del messaggio. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main>
      {/* Hero */}
      <section className="bg-[#09090B] pt-28 pb-20 md:pt-36 md:pb-28">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <p className="text-[#FF5200] text-sm font-bold tracking-widest uppercase mb-4 font-heading">
              Contatti
            </p>
            <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl font-bold text-white tracking-tight uppercase leading-[0.9]">
              Parliamo del
              <br />
              <span className="text-[#FF5200]">tuo progetto</span>
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-20 md:py-28 bg-white" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">
            {/* Form */}
            <div className="lg:col-span-3">
              <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight text-[#09090B] mb-8">
                Inviaci un messaggio
              </h2>
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-bold tracking-widest uppercase text-zinc-500 mb-2 font-heading">
                      Nome *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      required
                      className="w-full h-12 px-4 border-2 border-zinc-200 rounded-sm focus:border-[#FF5200] outline-none transition-colors duration-200 font-body"
                      placeholder="Il tuo nome"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold tracking-widest uppercase text-zinc-500 mb-2 font-heading">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      required
                      className="w-full h-12 px-4 border-2 border-zinc-200 rounded-sm focus:border-[#FF5200] outline-none transition-colors duration-200 font-body"
                      placeholder="la.tua@email.com"
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-bold tracking-widest uppercase text-zinc-500 mb-2 font-heading">
                    Telefono
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    className="w-full h-12 px-4 border-2 border-zinc-200 rounded-sm focus:border-[#FF5200] outline-none transition-colors duration-200 font-body"
                    placeholder="+39 XXX XXX XXXX"
                    data-testid="contact-phone-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold tracking-widest uppercase text-zinc-500 mb-2 font-heading">
                    Messaggio *
                  </label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full px-4 py-3 border-2 border-zinc-200 rounded-sm focus:border-[#FF5200] outline-none resize-none transition-colors duration-200 font-body"
                    placeholder="Descrivi la tua richiesta..."
                    data-testid="contact-message-input"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex items-center gap-2 bg-[#FF5200] text-white px-8 py-4 font-bold uppercase tracking-wide rounded-sm hover:bg-[#e64a00] transition-colors duration-200 font-heading text-sm disabled:opacity-50"
                  data-testid="contact-submit-btn"
                >
                  {loading ? (
                    <Loader2 size={18} className="animate-spin" />
                  ) : (
                    <Send size={18} />
                  )}
                  {loading ? "Invio..." : "Invia Messaggio"}
                </button>
              </form>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-2">
              <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight text-[#09090B] mb-8">
                Info di contatto
              </h2>
              <div className="space-y-6 mb-10">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#FF5200]/10 flex items-center justify-center flex-shrink-0">
                    <Phone size={22} className="text-[#FF5200]" />
                  </div>
                  <div>
                    <p className="font-bold text-[#09090B]">Telefono</p>
                    <p className="text-zinc-500">+39 334 770 8108</p>
                    <p className="text-zinc-500">+39 389 785 6034</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#FF5200]/10 flex items-center justify-center flex-shrink-0">
                    <Mail size={22} className="text-[#FF5200]" />
                  </div>
                  <div>
                    <p className="font-bold text-[#09090B]">Email</p>
                    <a href="mailto:info@mvtrasporti.com" className="text-zinc-500 hover:text-[#FF5200] transition-colors duration-200">info@mvtrasporti.com</a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#FF5200]/10 flex items-center justify-center flex-shrink-0">
                    <MapPin size={22} className="text-[#FF5200]" />
                  </div>
                  <div>
                    <p className="font-bold text-[#09090B]">Indirizzo</p>
                    <p className="text-zinc-500">Piazza Ugo Dallò, 23</p>
                    <p className="text-zinc-500">46043 Castiglione Delle Stiviere (MN)</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#FF5200]/10 flex items-center justify-center flex-shrink-0">
                    <Clock size={22} className="text-[#FF5200]" />
                  </div>
                  <div>
                    <p className="font-bold text-[#09090B]">Orari</p>
                    <p className="text-zinc-500">Lun - Ven: 08:00 - 18:00</p>
                    <p className="text-zinc-500">Sab: 08:00 - 13:00</p>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="border-2 border-zinc-200 overflow-hidden" data-testid="contact-map">
                <iframe
                  title="MV Trasporti - Posizione"
                  src="https://maps.google.com/maps?q=Piazza+Ugo+Dallo+23+Castiglione+delle+Stiviere+46043&t=&z=15&ie=UTF8&iwloc=&output=embed"
                  width="100%"
                  height="300"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
